import { MessageCircle } from "lucide-react";

export type SellerShopHeaderShop = {
  shopName: string;
  bio: string | null;
  avatarUrl: string | null;
  bannerUrl?: string | null;
  whatsappUrl: string | null;
  avgRating: number;
  ratingCount: number;
  listingCount: number;
};

export function SellerShopHeader({ shop }: { shop: SellerShopHeaderShop }) {
  const initial = shop.shopName.trim().charAt(0).toUpperCase() || "?";
  const ratingLabel =
    shop.ratingCount > 0 ? `${shop.avgRating.toFixed(1)} / 5` : "New";

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface">
      <div className="relative aspect-[3/1] w-full overflow-hidden bg-gradient-to-br from-brand-pink/40 via-brand-dark to-brand-blue/40 sm:aspect-[5/1]">
        {shop.bannerUrl ? (
          <img src={shop.bannerUrl} alt="" className="h-full w-full object-cover" />
        ) : null}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/50 to-transparent" />
      </div>
      <div className="flex flex-col gap-4 px-4 pb-4 sm:flex-row sm:items-end sm:justify-between sm:px-5 sm:pb-5">
        <div className="flex min-w-0 items-end gap-3.5">
          <div className="relative -mt-8 h-16 w-16 shrink-0 overflow-hidden rounded-full border-4 border-surface bg-surface-2 shadow-lg sm:-mt-10 sm:h-20 sm:w-20">
            {shop.avatarUrl ? (
              <img src={shop.avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center font-display text-xl font-bold text-white/50">
                {initial}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1 pt-2">
            <h1 className="font-display text-xl font-bold leading-tight text-foreground sm:text-2xl">{shop.shopName}</h1>
          </div>
        </div>
        {shop.whatsappUrl ? (
          <a
            href={shop.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-brand-pink/40 hover:bg-brand-pink/10"
          >
            <MessageCircle size={18} className="text-brand-pink" aria-hidden />
            Chat
          </a>
        ) : null}
      </div>
      <div className="px-4 pb-4 sm:px-5">
        {shop.bio ? (
          <p className="line-clamp-3 text-sm text-white/55">{shop.bio}</p>
        ) : (
          <p className="text-sm text-white/40">Collector shop on Mr Gaole marketplace</p>
        )}
      </div>
      <div className="grid grid-cols-3 divide-x divide-white/10 border-t border-white/10 bg-black/20">
        <ShopStat value={ratingLabel} label="Rating" />
        <ShopStat value={String(shop.ratingCount)} label="Reviews" />
        <ShopStat value={String(shop.listingCount)} label="Listings" />
      </div>
    </div>
  );
}

function ShopStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="px-3 py-3 text-center sm:py-3.5">
      <p className="font-display text-base font-bold text-foreground sm:text-lg">{value}</p>
      <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-white/45">{label}</p>
    </div>
  );
}

export function SellerShopHeaderSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface">
      <div className="aspect-[3/1] w-full animate-pulse bg-white/5 sm:aspect-[5/1]" />
      <div className="flex gap-3 p-5">
        <div className="-mt-12 h-16 w-16 animate-pulse rounded-full border-4 border-surface bg-white/10" />
        <div className="flex-1 space-y-2">
          <div className="h-6 w-48 animate-pulse rounded-lg bg-white/10" />
          <div className="h-4 w-full max-w-md animate-pulse rounded bg-white/5" />
        </div>
      </div>
    </div>
  );
}
